import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { HelpCircle, Mic, Navigation, Plus, MessageSquare, X } from 'lucide-react';

const VOICE_HELP_SHOWN_KEY = 'voice_help_shown';

/**
 * VoiceCommandHelp - Modal showing available voice commands
 * Shows automatically on first use, can be triggered manually via help icon
 */
export default function VoiceCommandHelp({ isOpen, onClose, showOnFirstUse = true }) {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Check if we should auto-show on first use
    if (showOnFirstUse && isOpen) {
      const hasSeenHelp = localStorage.getItem(VOICE_HELP_SHOWN_KEY);
      if (!hasSeenHelp) {
        setShowModal(true);
        localStorage.setItem(VOICE_HELP_SHOWN_KEY, 'true');
      }
    }
  }, [isOpen, showOnFirstUse]);

  // External control
  useEffect(() => {
    if (isOpen !== undefined) {
      setShowModal(isOpen);
    }
  }, [isOpen]);

  const handleClose = () => {
    setShowModal(false);
    onClose?.();
  };

  const commandCategories = [
    {
      title: 'Navigation',
      icon: Navigation,
      color: 'text-blue-500',
      commands: [
        { phrase: '"Go to dashboard"', description: 'Opens home dashboard' },
        { phrase: '"Open players"', description: 'Opens player roster' },
        { phrase: '"Show schedule"', description: 'Opens calendar/schedule' },
        { phrase: '"Navigate to practice"', description: 'Opens practice page' },
        { phrase: '"Videos" or "Film"', description: 'Opens video library' },
        { phrase: '"Stats" or "Statistics"', description: 'Opens stats tracking' },
        { phrase: '"Whiteboard"', description: 'Opens strategy whiteboard' },
        { phrase: '"Reports"', description: 'Opens reports page' },
        { phrase: '"Settings"', description: 'Opens settings' },
        { phrase: '"Contacts"', description: 'Opens contact cards' },
        { phrase: '"Messages" or "Chat"', description: 'Opens team chat' },
        { phrase: '"Social media"', description: 'Opens social media' },
      ]
    },
    {
      title: 'Quick Actions',
      icon: Plus,
      color: 'text-green-500',
      commands: [
        { phrase: '"Add player"', description: 'Opens add player dialog' },
        { phrase: '"Schedule event"', description: 'Opens new event form' },
        { phrase: '"Create practice plan"', description: 'Goes to practice planning' },
        { phrase: '"Upload video"', description: 'Opens video upload' },
        { phrase: '"Take attendance"', description: 'Opens attendance tracking' },
      ]
    },
    {
      title: 'Communication',
      icon: MessageSquare,
      color: 'text-purple-500',
      commands: [
        { phrase: '"Send message"', description: 'Opens messaging' },
        { phrase: '"New message"', description: 'Opens team chat' },
      ]
    },
    {
      title: 'Help',
      icon: HelpCircle,
      color: 'text-amber-500',
      commands: [
        { phrase: '"Help"', description: 'Shows quick command summary' },
        { phrase: '"What can you do"', description: 'Lists available commands' },
      ]
    }
  ];

  return (
    <Dialog open={showModal} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Mic className="h-6 w-6 text-primary" />
            Voice Commands Guide
          </DialogTitle>
          <DialogDescription>
            Click the floating microphone button and speak these commands to navigate and control the app hands-free.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {commandCategories.map((category) => (
            <div key={category.title} className="space-y-3">
              <div className="flex items-center gap-2">
                <category.icon className={`h-5 w-5 ${category.color}`} />
                <h3 className="font-semibold text-lg">{category.title}</h3>
              </div>
              <div className="grid gap-2 pl-7">
                {category.commands.map((cmd, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-start gap-3 p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <code className="text-sm font-medium text-primary bg-primary/10 px-2 py-1 rounded whitespace-nowrap">
                      {cmd.phrase}
                    </code>
                    <span className="text-sm text-muted-foreground pt-1">
                      {cmd.description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-6">
            <h4 className="font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
              <Mic className="h-4 w-4" />
              Tips for Best Results
            </h4>
            <ul className="text-sm text-blue-600 dark:text-blue-400 mt-2 space-y-1 list-disc list-inside">
              <li>Speak clearly at a normal pace</li>
              <li>Wait for the "Listening..." indicator before speaking</li>
              <li>Use simple phrases like "Go to players" or "Add player"</li>
              <li>Say "Help" anytime to see a quick command summary</li>
            </ul>
          </div>
        </div>

        <div className="flex justify-between items-center mt-6 pt-4 border-t">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="rounded"
              onChange={(e) => {
                if (e.target.checked) {
                  localStorage.setItem(VOICE_HELP_SHOWN_KEY, 'never');
                } else {
                  localStorage.setItem(VOICE_HELP_SHOWN_KEY, 'true');
                }
              }}
            />
            Don't show this again
          </label>
          <Button onClick={handleClose} data-testid="voice-help-close-btn">
            Got it!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * VoiceHelpButton - Small help icon button to open the voice command guide
 */
export function VoiceHelpButton({ onClick }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className="h-8 w-8 p-0 rounded-full"
      title="Voice command help"
      data-testid="voice-help-btn"
    >
      <HelpCircle className="h-4 w-4 text-muted-foreground" />
    </Button>
  );
}
